import { entityIds, type EntityIds, type Measurement, type Outcome } from "./measurements";

export type Readiness = "loading" | "ready";
type Attempt = {
  id: string;
  url: string;
  start: number;
  page: string;
  initial: boolean;
  committed: boolean;
  outcome?: Outcome;
  routeDepth?: number;
  entities?: EntityIds;
};

// Completion means tracked loading settled, not that the page succeeded.
export const createPageLifecycle = (
  emit: (measurement: Measurement) => void,
  now = () => performance.now()
) => {
  const loading = new Set<object>();
  const reduxLoads = new Map<string, object>();
  let reduxState: Record<string, boolean> = {};
  let current: Attempt | undefined;
  let deadline: ReturnType<typeof setTimeout> | undefined;
  let settle: ReturnType<typeof setTimeout> | undefined;
  let frame = 0;
  let hidden = false;
  let journeyId = "";

  const cancelCandidate = () => {
    clearTimeout(settle);
    cancelAnimationFrame(frame);
  };

  function syncReduxLoading(previous: Record<string, boolean>) {
    if (!current || current.outcome) {
      return;
    }
    let changed = false;
    for (const [key, pending] of Object.entries(reduxState)) {
      if (pending && !previous[key] && !reduxLoads.has(key)) {
        const load = {};
        reduxLoads.set(key, load);
        loading.add(load);
        changed = true;
      }
    }
    for (const [key, load] of reduxLoads) {
      if (!reduxState[key] && loading.delete(load)) {
        changed = true;
      }
    }
    if (changed) {
      reconcile();
    }
  }
  function finish(outcome: Outcome, at = now()) {
    if (!current || current.outcome) {
      return;
    }
    current.outcome = outcome;
    cancelCandidate();
    clearTimeout(deadline);
    const measurement = {
      type: "page_load_complete",
      timestamp: new Date(performance.timeOrigin + at).toISOString(),
      values: { duration_ms: Math.max(0, at - current.start) },
      context: {
        page: current.page,
        attempt_id: current.id,
        journey_id: journeyId,
        navigation_type: current.initial ? "initial" : "client",
        outcome,
        ...current.entities,
      },
    };
    emit(measurement);
  }
  function reconcile() {
    cancelCandidate();
    if (!current || current.outcome || !current.committed || loading.size) {
      return;
    }
    const owner = current;
    // Wait for paint, then confirm stability without counting the 500ms delay.
    frame = requestAnimationFrame(() => {
      frame = requestAnimationFrame(() => {
        const paintedAt = now();
        settle = setTimeout(() => {
          if (current === owner) {
            finish("completed", paintedAt);
          }
        }, 500);
      });
    });
  }
  return {
    reset() {
      cancelCandidate();
      clearTimeout(deadline);
      current = undefined;
      loading.clear();
      reduxLoads.clear();
      journeyId = "";
    },
    // Only loading cycles started during this visit can block it. Pending flags
    // inherited from another page have no route ownership and may never clear.
    reduxLoading(state: Record<string, boolean>) {
      if (state === reduxState) {
        return;
      }
      const previous = reduxState;
      reduxState = state;
      syncReduxLoading(previous);
    },
    routeName(name: string, depth: number, entities: EntityIds = {}) {
      if (current && !current.outcome && depth >= (current.routeDepth || 0)) {
        current.page = name;
        current.routeDepth = depth;
        current.entities = entityIds(entities);
      }
    },
    navigate(url: string, initial = false, replace = false) {
      // Exact-URL pushes and replacements intentionally stay in the current visit,
      // even after completion; a new history entry alone is not a measured page load.
      if (current?.url === url) {
        return;
      }
      if (replace && current) {
        const samePath = new URL(current.url).pathname === new URL(url).pathname;
        // Replacements within a page belong to that visit. Redirects before
        // completion retain its original start, including cold-load time.
        if (samePath || !current.outcome) {
          current.url = url;
          if (!samePath) {
            cancelCandidate();
            current.committed = false;
            current.page = "other";
            current.routeDepth = undefined;
            current.entities = undefined;
          }
          return;
        }
      }
      finish("canceled");
      journeyId ||= crypto.randomUUID();
      reduxLoads.forEach((load) => loading.delete(load));
      reduxLoads.clear();
      current = {
        id: crypto.randomUUID(),
        url,
        page: "other",
        start: initial ? 0 : now(),
        initial,
        committed: false,
      };
      deadline = setTimeout(() => finish("timeout"), 300_000);
      if (hidden) {
        finish("backgrounded");
      }
    },
    commit() {
      if (current) {
        current.committed = true;
        reconcile();
      }
    },
    signal(key: object, state?: Readiness) {
      if (loading.has(key) === (state === "loading")) {
        return;
      }
      if (state === "loading") {
        loading.add(key);
      } else {
        loading.delete(key);
      }
      reconcile();
    },
    visibility(isHidden: boolean) {
      hidden = isHidden;
      if (hidden) {
        finish("backgrounded");
      }
    },
    dispose() {
      cancelCandidate();
      clearTimeout(deadline);
      loading.clear();
    },
  };
};
